import React, { useCallback, useState, ChangeEvent, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { message, Input, Tooltip, Modal } from 'antd';
import { scMixinEllipsis } from '@utils/emotion/mixins';

import { IVideoStore } from '@stores/videoStore';
import { Chapters, IChapter } from '@interfaces/pose.interface';
import { HoveredChapter } from '@interfaces/video.interface';
import { formatDigitToTimeFrame, formatTimeToDigit, getTimeFormat } from '~/utils';
import {
  prevChapterData,
  currentChapterData,
  nextChapterData,
  MIN_GAP_SEC,
  DEFAULT_DURATION,
} from '~/utils/chapter';
import TimeInput from '@components/TimeInput';

import { ReactComponent as IconArrow } from '@svg/chevron_right.svg';
import { ReactComponent as IconDelete } from '@svg/icon-delete.svg';
import { ReactComponent as IconPlayCircle } from '@svg/common/icon-play-circle.svg';
import { ReactComponent as IconVector } from '@svg/icon-vector.svg';
import { ReactComponent as IconDownarrow } from '@svg/icon-chapterbox-downarrow.svg';
import useStores from '@lib/hooks/useStores';

interface ChapterBoxProps {
  editable: boolean;
  isFullscreen: boolean;
  duration: number;
  currentTime: number;
  chapterData: Chapters;
  updateChapterData?: (prevData: any) => void;
  onChapterClick?: (idx: number) => void;
  currentChapterIdx?: number;
  hoveredChapter: HoveredChapter;
  onChapterMouseOver: (data: HoveredChapter) => void;
  clickedChapter?: HoveredChapter;
  onChapterClickChapterEditor?: (data: HoveredChapter) => void;
}

const transcodingFrameRate = parseFloat(process.env.REACT_APP_TRANSCODING_FRAME_RATE ?? '');

const ChapterBox: React.FC<ChapterBoxProps> = ({
  editable,
  isFullscreen,
  duration,
  currentTime,
  chapterData,
  updateChapterData,
  onChapterClick,
  currentChapterIdx,
  hoveredChapter,
  onChapterMouseOver,
  clickedChapter,
  onChapterClickChapterEditor,
}: ChapterBoxProps) => {
  const { videoStore } = useStores<{ videoStore: IVideoStore }>();
  const { t } = useTranslation();
  const [currentRenameIdx, setRenameIdx] = useState<number | null>(null);
  const [currentRenameOption, setRenameOption] = useState<string | null>(null);
  const [currentRenameText, setRenameText] = useState('');
  const [timeDuration, setTimeDuration] = useState(10);
  const [isEditDuration, setIsEditDuration] = useState(false);
  const chapterListBox = useRef<HTMLDivElement | null>(null);
  const [currentPlayingChapterIdx, setCurrentPlayingChapterIdx] = useState<number | null>(null);

  useEffect(() => {
    function blurHandler(e: any): void {
      const path = e.path as any;
      if (
        path.some(
          (element: HTMLElement) =>
            ('classList' in element && element.classList.contains('time-picker')) ||
            ('classList' in element &&
              element.classList.contains('ant-input') &&
              currentRenameOption === 'name'),
        )
      )
        return;
      if (currentRenameIdx !== null) {
        setRenameIdx(null);
        setRenameOption(null);
      }
    }

    document.body.addEventListener('click', blurHandler);

    return () => {
      document.body.removeEventListener('click', blurHandler);
    };
  }, [currentRenameIdx, currentRenameOption]);

  const createChapter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (e.target !== e.currentTarget) return;
      if (!videoStore.playerRef) return;

      const prevChapter = prevChapterData(chapterData, currentTime);
      const currentChapter = currentChapterData(chapterData, currentTime)[0];
      const nextChapter = nextChapterData(chapterData, currentTime);
      const minBoundary = prevChapter ? prevChapter.endTime + MIN_GAP_SEC : 0;
      const maxBoundary = nextChapter ? nextChapter.startTime - MIN_GAP_SEC : duration;

      const newStartTime = currentChapter
        ? currentChapter.endTime + MIN_GAP_SEC
        : currentTime < minBoundary
        ? minBoundary
        : currentTime;
      const newEndTime = newStartTime + timeDuration;

      if (newEndTime > maxBoundary) {
        message.warn(t('videos.action.edit.chapter.box.warn'));
        return;
      }

      // [TODO] fix bug for chpater stacking when currentChapter time is null with between of prev and next chapter
      // console.log(currentChapter);

      updateChapterData &&
        updateChapterData((prevData: IChapter[]) => {
          const newChapterIdx = nextChapter ? nextChapter.chapterIdx : chapterData.length;
          const newChapter = {
            chapterIdx: newChapterIdx,
            label: t('videos.action.edit.chapter.box.new'),
            startTime: newStartTime,
            endTime: newEndTime,
            completed: false,
          };

          const _prevData = [...prevData];
          const newChapterData = _prevData.map((chapter) => ({
            ...chapter,
            chapterIdx:
              newChapterIdx <= chapter.chapterIdx ? chapter.chapterIdx + 1 : chapter.chapterIdx,
          }));
          newChapterData.splice(newChapterIdx, 0, newChapter);

          return newChapterData;
        });

      videoStore.playerRef.currentTime(newStartTime + 0.001);
    },
    [chapterData, currentTime, timeDuration, updateChapterData, t, videoStore.playerRef, duration],
  );

  const removeChapter = (chapterIdx: number) => {
    Modal.confirm({
      title: t('video.editor.chapter.delete.confirm.title'),
      content: t('video.editor.chapter.delete.confirm.description'),
      okText: t('a11y.ok'),
      cancelText: t('a11y.cancel'),
      onOk: () => {
        if (currentRenameIdx === chapterIdx) setRenameIdx(null);

        updateChapterData &&
          updateChapterData((prevData: Chapters) => {
            const newData = [...prevData].filter((v) => v.chapterIdx !== chapterIdx);

            for (let newIdx = 0; newIdx < newData.length; newIdx += 1) {
              newData[newIdx].chapterIdx = newIdx;
            }

            return newData;
          });
      },
      onCancel: () => null,
    });
  };

  const renameInputChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement> | string) => {
      const value = typeof e === 'string' ? e : (e.target.value as string);

      setRenameText(value);

      if (value.length > 0 && currentRenameOption === 'name') {
        updateChapterData &&
          updateChapterData((prevData: Chapters) => {
            const newData = [...prevData];
            const index = newData.findIndex((chapter) => chapter.chapterIdx === currentRenameIdx);

            newData[index].label = value;
            return newData;
          });
      } else if (value.length > 0 && currentRenameOption === 'start') {
        updateChapterData &&
          updateChapterData((prevData: Chapters) => {
            const newData = [...prevData];
            const index = newData.findIndex((chapter) => chapter.chapterIdx === currentRenameIdx);

            newData[index].startTime = isNaN(Number(value))
              ? formatTimeToDigit(value, transcodingFrameRate)
              : parseFloat(value);
            return newData;
          });
      } else if (value.length > 0 && currentRenameOption === 'end') {
        updateChapterData &&
          updateChapterData!((prevData: Chapters) => {
            const newData = [...prevData];
            const index = newData.findIndex((chapter) => chapter.chapterIdx === currentRenameIdx);

            newData[index].endTime = isNaN(Number(value))
              ? formatTimeToDigit(value, transcodingFrameRate)
              : parseFloat(value);
            return newData;
          });
      }
    },
    [currentRenameIdx, currentRenameOption, updateChapterData],
  );

  const renameInputBlurHandler = useCallback(
    (timeValue?: string) => {
      if (timeValue) {
        renameInputChangeHandler(timeValue);
      }

      setRenameIdx(null);
      setRenameText('');
      setRenameOption('');
    },
    [renameInputChangeHandler],
  );

  const renameInputSubmitHandler = useCallback(() => {
    if (currentRenameText.length > 0 && currentRenameOption === 'name') {
      updateChapterData &&
        updateChapterData((prevData: Chapters) => {
          const newData = [...prevData];
          const index = newData.findIndex((chapter) => chapter.chapterIdx === currentRenameIdx);

          newData[index].label = currentRenameText;
          setRenameIdx(null);
          setRenameText('');
          setRenameOption('');
          return newData;
        });
    } else if (currentRenameText.length > 0 && currentRenameOption === 'start') {
    } else if (currentRenameText.length > 0 && currentRenameOption === 'end') {
    } else {
      message.warning(t('video.editor.chapter.rename.empty'));
    }
  }, [currentRenameText, currentRenameOption, updateChapterData, currentRenameIdx, t]);

  const addDuration = (seconds: number) => {
    let totalSeconds = timeDuration;

    totalSeconds += seconds;

    totalSeconds =
      totalSeconds >= duration
        ? duration - 0.001
        : totalSeconds > 86399
        ? Number(86399)
        : totalSeconds;

    setTimeDuration(totalSeconds);
  };

  const timeInputChangeHandler = (timeValue: string | null) => {
    if (!timeValue) return;
    setIsEditDuration(false);

    let newDuration = timeValue.indexOf(':') < 0 ? parseFloat(timeValue) : timeValue;

    if (typeof newDuration === 'string') {
      newDuration = formatTimeToDigit(newDuration, transcodingFrameRate);
    }

    setTimeDuration(newDuration);
  };

  const refreshDuration = () => {
    setTimeDuration(DEFAULT_DURATION);
  };

  useEffect(() => {
    if (
      hoveredChapter.from === 'timeline' &&
      hoveredChapter.idx > -1 &&
      chapterListBox !== undefined &&
      chapterListBox.current !== null
    ) {
      const nowOffsetTop = (chapterListBox.current.children[hoveredChapter.idx] as HTMLDivElement)
        ?.offsetTop;

      if (!nowOffsetTop) return;

      if (nowOffsetTop < chapterListBox.current.scrollTop) {
        chapterListBox.current?.scrollTo({ top: nowOffsetTop, behavior: 'smooth' });
      } else if (
        nowOffsetTop + chapterListBox.current.children[hoveredChapter.idx].clientHeight >
        chapterListBox.current.scrollTop + chapterListBox.current.clientHeight
      ) {
        chapterListBox.current?.scrollTo({ top: nowOffsetTop, behavior: 'smooth' });
      }
    }
  }, [hoveredChapter.from, hoveredChapter.idx]);

  const pauseWhenTimeReached = useCallback(() => {
    if (!videoStore.playerRef || currentPlayingChapterIdx === null) return;
    const currentPlayingChapter = chapterData[currentPlayingChapterIdx];

    if (
      videoStore.playerRef.currentTime() > currentPlayingChapter.endTime &&
      videoStore.playerRef.currentTime() < currentPlayingChapter.endTime + 0.249
    ) {
      videoStore.playerRef.off('timeupdate', pauseWhenTimeReached);
      videoStore.playerRef.pause();
      videoStore.playerRef.currentTime(currentPlayingChapter.startTime);
      setCurrentPlayingChapterIdx(null);
    } else if (videoStore.playerRef!.currentTime() > currentPlayingChapter.endTime) {
      setCurrentPlayingChapterIdx(null);
      videoStore.playerRef.off('timeupdate', pauseWhenTimeReached);
    }
  }, [currentPlayingChapterIdx, chapterData, videoStore.playerRef]);

  useEffect(() => {
    if (!videoStore.playerRef || currentPlayingChapterIdx === null || !chapterData.length) return;
    const currentPlayingChapter = chapterData[currentPlayingChapterIdx];
    videoStore.playerRef.on('timeupdate', pauseWhenTimeReached);

    videoStore.playerRef.currentTime(currentPlayingChapter.startTime);
    videoStore.playerRef.play();
  }, [currentPlayingChapterIdx, chapterData, pauseWhenTimeReached, videoStore.playerRef]);

  const playChapter = useCallback(
    (chapterIdx: number) => {
      if (!videoStore.playerRef || currentPlayingChapterIdx === chapterIdx) return;

      setCurrentPlayingChapterIdx(null);
      videoStore.playerRef.pause();
      videoStore.playerRef.off('timeupdate', pauseWhenTimeReached);
      setCurrentPlayingChapterIdx(chapterIdx);
    },
    [currentPlayingChapterIdx, pauseWhenTimeReached, videoStore.playerRef],
  );

  return (
    <ChapterBoxBlock
      editable={editable}
      durationInputFocused={isEditDuration}
      hoverChapterIdx={hoveredChapter.idx}
      clickChapterIdx={clickedChapter ? clickedChapter.idx : -1}
    >
      <h2 className="title">
        {t(
          isFullscreen
            ? 'videos.action.edit.chapter.box.title2'
            : 'videos.action.edit.chapter.box.title',
        )}
      </h2>
      <div className="chapter-list" ref={chapterListBox}>
        {!chapterData.length && (
          <div className="no-item">
            <div className="message-box">
              <p
                className="message"
                dangerouslySetInnerHTML={{
                  __html: t('video.editor.chapter.noitem'),
                }}
              />
              <i className="icon" aria-hidden="true">
                <IconDownarrow />
              </i>
            </div>
          </div>
        )}
        {chapterData.map((chapter) => (
          <div
            className="chapter-item"
            data-edit={currentRenameIdx === chapter.chapterIdx ? 'true' : 'false'}
            key={chapter.chapterIdx}
            onClick={() => {
              if (onChapterClick) onChapterClick(chapter.chapterIdx);
              if (onChapterClickChapterEditor)
                onChapterClickChapterEditor({
                  idx: chapter.chapterIdx,
                  from: 'chapterbox',
                });
            }}
            onMouseOver={() => {
              onChapterMouseOver({ idx: chapter.chapterIdx, from: 'chapterbox' });
            }}
            onMouseLeave={() => {
              onChapterMouseOver({ idx: -1, from: null });
            }}
            aria-selected={
              onChapterClick
                ? currentChapterIdx === chapter.chapterIdx
                  ? 'true'
                  : 'false'
                : undefined
            }
          >
            <div className="chapter-label">
              <button
                className="btn-chapter-play"
                aria-label="챕터 재생"
                tabIndex={0}
                onClick={() => playChapter(chapter.chapterIdx)}
              >
                <IconPlayCircle width="18" height="18" />
              </button>
              {currentRenameIdx === chapter.chapterIdx && currentRenameOption === 'name' ? (
                <Input
                  style={{
                    width: 'calc(100% - 45px)',
                  }}
                  defaultValue={currentRenameText}
                  value={currentRenameText}
                  onChange={(e) => renameInputChangeHandler(e)}
                  onBlur={() => renameInputBlurHandler()}
                  onPressEnter={() => renameInputSubmitHandler()}
                />
              ) : (
                <strong
                  className="chapter-label-text"
                  onClick={() => {
                    if (editable) {
                      setRenameText(chapter.label);
                      setRenameIdx(chapter.chapterIdx);
                      setRenameOption('name');
                    }
                  }}
                >
                  {chapter.label}
                </strong>
              )}
            </div>
            <div className="chapter-time">
              <div className="chapter-time-item">
                <span className="time-label">{t('videos.action.edit.chapter.box.start')}</span>
                {currentRenameIdx === chapter.chapterIdx && currentRenameOption === 'start' ? (
                  <TimeInput
                    type="modify"
                    changeEvent={(timeValue) => {
                      if (timeValue) {
                        setRenameText(timeValue);
                      }
                    }}
                    blurEvent={(timeValue) => {
                      renameInputBlurHandler(timeValue);
                    }}
                    hasRefresh={true}
                    duration={duration}
                    value={chapter.startTime}
                    defaultValue={formatDigitToTimeFrame(
                      chapter.startTime,
                      transcodingFrameRate,
                      getTimeFormat(duration),
                    )}
                    transcodingFrameRate={transcodingFrameRate}
                    isEditDuration={isEditDuration}
                    timeFormat={getTimeFormat(duration)}
                    chapterData={chapterData}
                    currentRenameIdx={currentRenameIdx}
                    isStart={true}
                  />
                ) : (
                  <>
                    <span
                      className="time-value time-start"
                      onClick={() => {
                        if (editable) {
                          setRenameText(
                            formatDigitToTimeFrame(
                              chapter.startTime,
                              transcodingFrameRate,
                              getTimeFormat(duration),
                            ),
                          );
                          setRenameIdx(chapter.chapterIdx);
                          setRenameOption('start');
                        }
                      }}
                    >
                      {formatDigitToTimeFrame(
                        chapter.startTime,
                        transcodingFrameRate,
                        getTimeFormat(duration),
                      )}
                    </span>
                    <i
                      className="icon"
                      onClick={() => {
                        if (videoStore.playerRef)
                          videoStore.playerRef.currentTime(chapter.startTime);
                      }}
                    >
                      <IconArrow width="8" height="10" />
                    </i>
                  </>
                )}
              </div>
              <div className="chapter-time-item">
                <span className="time-label">{t('videos.action.edit.chapter.box.end')}</span>
                {currentRenameIdx === chapter.chapterIdx && currentRenameOption === 'end' ? (
                  <TimeInput
                    type="modify"
                    changeEvent={(timeValue) => {
                      if (timeValue) {
                        setRenameText(timeValue);
                      }
                    }}
                    blurEvent={(timeValue) => {
                      renameInputBlurHandler(timeValue);
                    }}
                    hasRefresh={true}
                    duration={duration}
                    value={chapter.endTime}
                    defaultValue={formatDigitToTimeFrame(
                      chapter.endTime,
                      transcodingFrameRate,
                      getTimeFormat(duration),
                    )}
                    transcodingFrameRate={transcodingFrameRate}
                    isEditDuration={isEditDuration}
                    timeFormat={getTimeFormat(duration)}
                    chapterData={chapterData}
                    currentRenameIdx={currentRenameIdx}
                    isStart={false}
                  />
                ) : (
                  <>
                    <span
                      className="time-value time-start"
                      onClick={() => {
                        if (editable) {
                          setRenameText(
                            formatDigitToTimeFrame(
                              chapter.endTime,
                              transcodingFrameRate,
                              getTimeFormat(duration),
                            ),
                          );
                          setRenameIdx(chapter.chapterIdx);
                          setRenameOption('end');
                        }
                      }}
                    >
                      {formatDigitToTimeFrame(
                        chapter.endTime,
                        transcodingFrameRate,
                        getTimeFormat(duration),
                      )}
                    </span>
                    <i
                      className="icon"
                      onClick={() => {
                        if (videoStore.playerRef) {
                          setCurrentPlayingChapterIdx(null);
                          videoStore.playerRef.pause();
                          videoStore.playerRef.currentTime(chapter.endTime);
                        }
                      }}
                    >
                      <IconArrow width="8" height="10" />
                    </i>
                  </>
                )}
              </div>
            </div>
            <div className="chapter-item-action">
              {clickedChapter && clickedChapter.idx === chapter.chapterIdx && (
                <div role="button" className="btn-unfix" tabIndex={0}>
                  <i className="icon">
                    <IconVector width="15" height="15" />
                  </i>
                </div>
              )}
              {editable && (
                <button
                  className="btn-chapter-remove"
                  aria-label={t('videos.action.edit.chapter.box.delete')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeChapter(chapter.chapterIdx);
                  }}
                >
                  <i className="icon">
                    <IconDelete width="12" height="15" />
                  </i>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {editable && duration > 0 && (
        <Tooltip
          placement="top"
          arrowPointAtCenter={true}
          title={t('videos.action.edit.chapter.box.tip1')}
          color="#52c41a"
          overlayClassName="font-size-15 text-dark align-center"
          overlayStyle={{ maxWidth: 245 }}
          trigger={['focus', 'hover']}
        >
          <div className="chapter-list-action">
            <div className="time-duration-input">
              <TimeInput
                type="new"
                focusEvent={() => {
                  setIsEditDuration(true);
                }}
                refreshEvent={refreshDuration}
                changeEvent={(timeValue) => {
                  timeInputChangeHandler(timeValue);
                }}
                hasRefresh={true}
                duration={duration}
                value={timeDuration}
                defaultValue={
                  getTimeFormat(duration) === 'h:mm:ss:ff'
                    ? '0:00:10:00'
                    : getTimeFormat(duration) === 'mm:ss:ff'
                    ? '00:10:00'
                    : '10:00'
                }
                transcodingFrameRate={transcodingFrameRate}
                isEditDuration={isEditDuration}
                timeFormat={getTimeFormat(duration)}
              />
              <div className="time-duration-btn">
                <button className="btn-plus-duration" onClick={() => addDuration(1)}>
                  +1s
                </button>
                <button className="btn-plus-duration" onClick={() => addDuration(5)}>
                  +5s
                </button>
                <button className="btn-plus-duration" onClick={() => addDuration(10)}>
                  +10s
                </button>
                <button className="btn-plus-duration" onClick={() => addDuration(30)}>
                  +30s
                </button>
                <button className="btn-plus-duration" onClick={() => addDuration(60)}>
                  +60s
                </button>
              </div>
            </div>

            <button
              className="btn-chapter-create"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createChapter(e);
              }}
            >
              <span className="icon-plus"></span>
              {t('videos.action.edit.chapter.box.add')}
            </button>
          </div>
        </Tooltip>
      )}
    </ChapterBoxBlock>
  );
};

interface ChapterBoxBlockProps {
  editable: boolean;
  durationInputFocused: boolean;
  hoverChapterIdx: number;
  clickChapterIdx: number;
}

const ChapterBoxBlock = styled.div<ChapterBoxBlockProps>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 256px;
  height: 100%;
  border-bottom-style: solid;
  border-bottom-color: ${({ durationInputFocused }) =>
    durationInputFocused ? '#52c41a' : '#4f4f4f'};
  border-bottom-width: ${({ editable }) => (editable ? '0' : '1px')};
  color: #b6b7b8;

  .title {
    margin: 0 !important;
    padding: 8px 10px;
    border: 1px solid #4f4f4f;
    font-weight: bold;
    font-size: 12px;
    line-height: 18px;
    color: #b6b7b8;
  }

  .chapter-list-action {
    border: 1px solid #52c41a;

    .time-duration-input {
      padding: 11px 0;
      text-align: center;

      .time-picker {
        width: 100px;
        padding: 4px 4px;
        border: 1px solid #424242;
        box-sizing: border-box;
        border-radius: 3px;
        background-color: transparent;
        text-align: center;
        font-weight: bold;
        color: #f2f3f6;

        &:invalid {
          box-shadow: none;
        }
      }

      .time-duration-btn {
        margin-top: 10px;

        .btn-plus-duration {
          width: 40px;
          margin-right: 7px;
          padding: 5px 0;
          background: #424242;
          border-radius: 3px;
          font-weight: bold;
          font-size: 12px;
          line-height: 18px;
          text-align: center;
          letter-spacing: 0.01em;
          color: #b6b7b8;
          cursor: pointer;

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }

    .btn-chapter-create {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      padding: 8px 0;
      border-top: 0.5px solid
        ${({ durationInputFocused }) => (durationInputFocused ? '#52c41a' : '#4f4f4f')};
      background-color: #52c41a;
      font-weight: bold;
      text-align: center;
      transition: background-color 100ms ease;
      color: #090909;
      cursor: pointer;

      &:hover,
      &:focus {
        background-color: #59d31d;
      }

      .icon-plus {
        display: inline-block;
        width: 18px;
        height: 18px;
        margin-right: 7px;
        border-radius: 3px;
        background: linear-gradient(#52c41a, #52c41a), linear-gradient(#52c41a, #52c41a), #090909;
        background-position: center;
        background-size: 50% 2px, 2px 50%; /*thickness = 2px, length = 50% (25px)*/
        background-repeat: no-repeat;
        pointer-events: none;
      }
    }
  }

  .chapter-list {
    position: relative;
    flex: 1;
    height: 100%;
    overflow-y: auto;
    border-left: 1px solid #4f4f4f;
    border-right: 1px solid #4f4f4f;

    .no-item {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      font-weight: bold;
      font-size: 12px;
      line-height: 1.25;

      .message {
        text-align: center;

        .label {
          color: #52c41a;
        }

        .icon {
          display: inline-block;
          position: relative;
          width: 13px;
          height: 13px;
          margin: 0 2px 0 4px;
          border-radius: 2px;
          background-color: #52c41a;
          vertical-align: -2px;

          &::before,
          &::after {
            display: block;
            position: absolute;
            background-color: #131313;
            content: '';
          }

          &::before {
            top: 6px;
            left: 3px;
            width: 7px;
            height: 1px;
          }

          &::after {
            top: 3px;
            left: 6px;
            width: 1px;
            height: 7px;
          }
        }
      }

      .icon {
        display: block;
        text-align: center;
        margin-top: 16px;
      }
    }
  }

  .chapter-item {
    position: relative;
    padding: 10px 10px 17px;
    border-bottom: 1px solid #4f4f4f;
    transition: background-color 100ms ease;

    &:hover,
    &:focus,
    &[data-edit='true'],
    &[aria-selected='true'],
    &:nth-of-type(${({ hoverChapterIdx, clickChapterIdx }) =>
          hoverChapterIdx >= 0 ? hoverChapterIdx + 1 : clickChapterIdx + 1}) {
      background-color: #5100ce;
      color: #f5f6f8;

      .chapter-label {
        padding-right: ${(props) => (props.editable ? '28px' : '0')};
      }

      .chapter-label-text {
        color: #f5f6f8;
      }

      svg g path,
      svg path {
        fill: #fff;
      }
    }

    &[data-edit='true'] {
      .chapter-label::before {
        display: none;
      }

      .chapter-item-action {
        top: 17px;
      }
    }

    &:not([aria-selected]) {
      &:hover,
      &:focus {
        background-color: #5100ce;

        .chapter-item-action {
          opacity: 1;
        }
      }
    }

    &[aria-selected] {
      cursor: pointer;
    }
  }

  .chapter-label {
    display: flex;
    position: relative;
    align-items: center;

    &::before {
      display: ${({ editable }) => (editable ? 'block' : 'none')};
      overflow: hidden;
      position: absolute;
      top: -3px;
      right: -6px;
      bottom: -4px;
      left: -6px;
      border-radius: 4px;
      background-color: rgba(255, 255, 255, 0.2);
      opacity: 0;
      pointer-events: none;
      transition: opacity 100ms ease;
      content: '';
    }

    &:hover::before {
      opacity: 1;
    }

    .chapter-label-text {
      ${scMixinEllipsis};
      display: block;
      position: relative;
      font-weight: bold;
      font-size: 16px;
      line-height: 1.25;
      color: #b6b7b8;
      cursor: ${({ editable }) => (editable ? 'pointer' : 'text')};
    }

    .icon {
      display: inline-block;
      margin-left: 4px;
      vertical-align: top;
    }

    .btn-chapter-play {
      position: relative;
      display: block;
      top: 1px;
      height: 18px;
      min-width: 18px;
      margin-right: 9px;
      overflow: hidden;
      cursor: pointer;
    }
  }

  .chapter-time {
    margin-top: 10px;

    .time-label {
      display: inline-block;
      min-width: 85px;
    }

    .chapter-time-item {
      .icon {
        position: relative;
        margin-left: 15px;
        cursor: pointer;
      }

      .time-value {
        cursor: ${({ editable }) => (editable ? 'pointer' : 'default')};
      }

      .time-picker {
        /* height: 24px; */
        background-color: #090909;
      }
    }

    .chapter-time-input {
      height: 24px;
      width: 90px;
    }
  }

  .chapter-item-action {
    display: flex;
    flex-direction: row;
    position: absolute;
    top: 13px;
    right: 9px;
    opacity: 0;
    transition: opacity 200ms ease;

    .btn-chapter-remove,
    .btn-unfix {
      padding: 4px;
      cursor: pointer;
      outline: none;

      .icon {
        display: block;
        position: relative;
        top: -4px;
        line-height: 0;

        svg path {
          fill: #fff;
        }
      }
    }
  }
`;

export default ChapterBox;
